"use client";

import { useEffect, useRef } from "react";
import { useTranslations } from "next-intl";
import { Button } from "../ui/Button";
import { useEmailVerificationReminder } from "@/app/modules/auth/hooks/useEmailVerificationReminder";
import styles from "./EmailVerificationReminder.module.css";

export function EmailVerificationReminder({ onHeightChanged }: { onHeightChanged: (height: number) => void }) {
  const t = useTranslations("auth.components.emailVerificationReminder");
  const reminder = useEmailVerificationReminder();
  const elementRef = useRef<HTMLDivElement>(null);
  const hasUser = Boolean(reminder.user);
  const open = reminder.visible && !reminder.dismissing;

  useEffect(() => {
    const element = elementRef.current;
    if (!element) {
      onHeightChanged(0);
      return;
    }

    function measure() {
      if (element) onHeightChanged(element.getBoundingClientRect().height);
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => {
      observer.disconnect();
      onHeightChanged(0);
    };
  }, [onHeightChanged, hasUser]);

  if (!reminder.user) return null;

  return (
    <div ref={elementRef} className={styles.viewport} data-open={open} aria-hidden={!open} inert={!open}>
      <div className={styles.clip}>
        <div className={styles.content}>
          <div className={styles.card} role="region" aria-label={t("title")}>
            <div className={styles.icon} aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="5" width="18" height="14" rx="3" />
                <path d="m4 7 6.2 4.8a3 3 0 0 0 3.6 0L20 7" />
              </svg>
            </div>
            <div className={styles.copy}>
              <p className={styles.title}>{t("title")}</p>
              <p className={styles.description}>
                {t.rich("description", {
                  email: reminder.user.email,
                  address: (chunks) => <strong className={styles.email}>{chunks}</strong>,
                })}
              </p>
              {reminder.feedback ? (
                <p className={styles.feedback} data-success={reminder.feedback === "sent"} role={reminder.feedback === "sent" ? "status" : "alert"}>
                  {t(reminder.feedback)}
                </p>
              ) : null}
            </div>
            <div className={styles.actions}>
              <Button type="button" variant="primary" size="sm" className={styles.resend} onClick={reminder.resend} isLoading={reminder.sending} disabled={reminder.resendDisabled || reminder.dismissing}>
                {!reminder.sending ? (
                  <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 7v5h-5M4 17v-5h5" />
                    <path d="M6.1 7a7 7 0 0 1 11.6-1L20 9M4 15l2.3 3A7 7 0 0 0 17.9 17" />
                  </svg>
                ) : null}
                {t(reminder.resendCoolingDown ? "resendCooldown" : "resend")}
              </Button>
            </div>
            <button type="button" onClick={reminder.close} disabled={reminder.dismissing} aria-label={t("dismiss")} title={t("dismiss")} className={styles.dismiss}>
              <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="m6 6 12 12M6 18 18 6" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
